import React, { useState } from 'react';
import { FloatingLabel, Form, Spinner } from 'react-bootstrap';

import './FloatingComboBoxInput.scss';

export interface FloatingComboBoxInputOption {
  id: string;
  label: string;
}

/**
 * Les paramètres nécessaires à l'affichage du composant
 */
export interface FloatingComboBoxInputProps {
  /**
   * L'identifiant unique du composant
   */
  id: string;

  /**
   * L'identifiant utilisé pour tester le composant
   */
  testId?: string;

  /**
   * Le nom donné à la boîte de séletion. Ce paramètre est affiché à l'utilisateur
   */
  label: string;

  /**
   * La valeur affiché dans la boîte de texte
   */
  value: string;

  /**
   * L'action à prendre lorsque l'utilisateur interagit avec la zone de texte
   * @param input L'état de la boîte de texte
   * @returns Ne retourne rien
   */
  onChange: (input: React.ChangeEvent<HTMLInputElement>) => void;

  /**
   * L'action à prendre lorsque l'utilisateur appuie sur une touche du clavier.
   *
   * L'événement est optionnel.
   *
   * @param event L'événement React.
   */
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;

  /**
   * Drapeau indiquant l'obligation d'utilisation du champ
   */
  required: boolean;

  /**
   * Le texte affiché à l'écran si l'état du composant est invalide
   *
   * Ce paramètre est optionnel
   */
  invalidFeedback?: string;

  /**
   * Un texte d'aide pouvant être affiché à l'utilisateur sous le composant
   *
   * Ce paramètre est optionnel
   */
  helpBlock?: string;

  /**
   * Le type de champ rendu disponible à l'utilisateur.
   *
   * Ce paramètre est optionnel.
   * Valeur par défaut : 'text'
   */
  type?: string;

  /**
   * L'élément rendu disponible à l'utilisateur.
   *
   * Ce paramètre est optionnel.
   * Valeur par défaut : 'input'
   */
  as?: React.ElementType;

  /**
   * Les propriétés CSS à appliquer au FloatingLabel
   */
  className?: string;

  /**
   * Indique si le champ doit être rendu en lecture seule.
   *
   * Ce paramètre est optionnel.
   */
  readOnly?: boolean;

  /**
   * Les options affichées sous le champ.
   */
  options: FloatingComboBoxInputOption[];

  /**
   * Indique si la liste des options est en cours de chargement.
   */
  loading?: boolean;

  /**
   * L'action à prendre lorsqu'une option est sélectionnée.
   */
  onOptionSelect: (option: FloatingComboBoxInputOption) => void;

}

/**
 * Ce composant permet l'affichage d'une boîte de sélection avec une boîte de texte de type `FloatingLabel`
 * standardisé, pour l'utilisation au sein de notre application.
 * @param props Les différents paramètres nécessaire à l'affichage du composant
 * @returns Le composant rendu par React
 */
export default function FloatingComboBoxInput(
  props: FloatingComboBoxInputProps
): React.ReactElement {
  const [isOpen, setOpen] = useState(false);

  return (
    <div
      className="position-relative"
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          setOpen(false);
        }
      }}
    >
      <FloatingLabel
        controlId={props.id}
        label={props.label}
        className={props.className}
      >
        <Form.Control
          data-testid={props.testId}
          type={props.type ?? 'text'}
          as={props.as ?? 'input'}
          aria-label={`${props.id}-${props.label}`}
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={`${props.id}-combo-options`}
          value={props.value}
          onChange={event => {
            props.onChange(event as React.ChangeEvent<HTMLInputElement>);
            setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
          }}
          required={props.required}
          readOnly={props.readOnly}
          plaintext={props.readOnly}
          autoComplete="off"
        />
        {props.invalidFeedback && (
          <Form.Control.Feedback type="invalid">
            {props.invalidFeedback}
          </Form.Control.Feedback>
        )}
        {props.helpBlock && (
          <Form.Text id={`${props.id}-helpblock`} muted>
            {props.helpBlock}
          </Form.Text>
        )}
      </FloatingLabel>

      {isOpen && (
        <div
          id={`${props.id}-combo-options`}
          className="dropdown-menu show w-100 floating-combobox-options"
        >
          {props.loading ? (
            <div className="px-3 py-2 text-muted">
              <Spinner animation="border" size="sm" className="me-2" />
              Chargement...
            </div>
          ) : props.options.length > 0 ? (
            props.options.map(option => (
              <button
                key={option.id}
                type="button"
                className="dropdown-item"
                onMouseDown={event => {
                  event.preventDefault();
                }}
                onClick={() => {
                  props.onOptionSelect(option);
                  setOpen(false);
                }}
              >
                {option.label}
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-muted">
              Aucune option trouvée
            </div>
          )}
        </div>
      )}
    </div>
  );
}
